/**
 * Centralized state management for the Warehouse Layout Editor.
 * Replaces 13+ useState hooks with a single useReducer.
 * Includes undo/redo history for all layout mutations.
 */

import { useReducer, useMemo, useCallback } from 'react';
import {
  WarehouseLayout,
  RackPosition,
  RackDimensions,
  RackSummary,
  EditorState,
  ViewMode,
  PlacementConfig,
  PreviewRackPosition,
  HeatmapData,
  LocationActivity,
  LayoutMetrics,
  DEFAULT_EDITOR_STATE,
  DEFAULT_PLACEMENT_CONFIG,
} from '../types/warehouseLayout';
import { calculateRackDimensions } from '../utils/positionCalculator';
import { sortRacksByNumber } from '../utils/bulkPlacement';

// ============================================
// Action Types
// ============================================

export type LayoutAction =
  | { type: 'LOAD_DATA_START' }
  | { type: 'LOAD_DATA_SUCCESS'; layout: WarehouseLayout; rackSummaries: RackSummary[] }
  | { type: 'LOAD_DATA_FAILURE'; error: string; fallbackLayout: WarehouseLayout }
  | { type: 'SET_LAYOUT'; layout: WarehouseLayout; pushHistory?: boolean }
  | { type: 'ADD_RACK'; rack: RackPosition }
  | { type: 'ADD_RACKS'; racks: RackPosition[] }
  | { type: 'REMOVE_RACK'; rackNumber: string }
  | { type: 'ROTATE_RACK'; rackNumber: string }
  | { type: 'MOVE_RACKS'; updates: Array<{ rackNumber: string; x: number; y: number }> }
  | { type: 'DELETE_SELECTED' }
  | { type: 'ROTATE_SELECTED' }
  | { type: 'ALIGN_SELECTED_TO_GRID' }
  | { type: 'SET_ANCHOR'; rackNumber: string | null }
  | { type: 'SET_SELECTION'; selectedRacks: Set<string> }
  | { type: 'SET_VIEW_MODE'; mode: ViewMode }
  | { type: 'TOGGLE_EDITOR_OPTION'; option: 'showGrid' | 'showLabels' | 'showHeatmap' | 'showActivity' }
  | { type: 'SET_ZOOM'; zoom: number }
  | { type: 'SET_PAN_OFFSET'; offset: { x: number; y: number } }
  | { type: 'SET_RUBBER_BAND'; rubberBand: EditorState['rubberBand'] }
  | { type: 'SET_PLACEMENT_CONFIG'; config: PlacementConfig }
  | { type: 'SET_PREVIEW_POSITIONS'; positions: PreviewRackPosition[] }
  | { type: 'APPLY_BULK_PLACEMENT'; positions: RackPosition[] }
  | { type: 'CANCEL_BULK_PLACEMENT' }
  | { type: 'TOGGLE_UNPLACED_SELECTION'; rackNumber: string }
  | { type: 'SELECT_ALL_UNPLACED' }
  | { type: 'DESELECT_ALL_UNPLACED' }
  | { type: 'TOGGLE_PLACEMENT_PANEL' }
  | { type: 'SET_ERROR'; error: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_SUCCESS' }
  | { type: 'SAVE_FAILURE' }
  | { type: 'TOGGLE_SETTINGS' }
  | { type: 'UPDATE_DIMENSIONS'; field: 'length' | 'width' | 'height'; value: number }
  | { type: 'RESET_LAYOUT'; emptyLayout: WarehouseLayout }
  | { type: 'IMPORT_LAYOUT'; layout: WarehouseLayout }
  | { type: 'OPEN_QUICK_LAYOUT' }
  | { type: 'CLOSE_QUICK_LAYOUT' }
  | { type: 'APPLY_SMART_LAYOUT'; positions: RackPosition[]; metrics: LayoutMetrics }
  | { type: 'SET_SIDEBAR_DRAG_PREVIEW'; preview: PreviewRackPosition | null }
  | { type: 'UNDO' }
  | { type: 'REDO' };

// ============================================
// State
// ============================================

export interface LayoutEditorState {
  // Data
  layout: WarehouseLayout | null;
  rackSummaries: RackSummary[];
  unplacedRacks: RackSummary[];

  // UI state
  editorState: EditorState;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  loading: boolean;
  error: string | null;
  showSettings: boolean;

  // Bulk placement
  showPlacementPanel: boolean;
  selectedUnplacedRacks: string[];
  previewPositions: PreviewRackPosition[];

  // Smart layout
  showQuickLayout: boolean;
  layoutMetrics: LayoutMetrics | null;

  // Drag from sidebar
  sidebarDragPreview: PreviewRackPosition | null;

  // History (undo/redo)
  history: WarehouseLayout[];
  historyIndex: number;
}

const MAX_HISTORY = 50;

export function createInitialState(): LayoutEditorState {
  return {
    layout: null,
    rackSummaries: [],
    unplacedRacks: [],
    editorState: DEFAULT_EDITOR_STATE,
    saveStatus: 'idle',
    loading: true,
    error: null,
    showSettings: false,
    showPlacementPanel: false,
    selectedUnplacedRacks: [],
    previewPositions: [],
    showQuickLayout: false,
    layoutMetrics: null,
    sidebarDragPreview: null,
    history: [],
    historyIndex: -1,
  };
}

// ============================================
// Helpers
// ============================================

function computeUnplaced(layout: WarehouseLayout | null, summaries: RackSummary[]): RackSummary[] {
  if (!layout || summaries.length === 0) return [];
  const placedSet = new Set(layout.racks.map(r => r.rackNumber));
  return sortRacksByNumber(summaries.filter(s => !placedSet.has(s.rackNumber)));
}

function pushHistory(state: LayoutEditorState): LayoutEditorState {
  if (!state.layout) return state;
  // Trim future history if we branched
  const trimmed = state.history.slice(0, state.historyIndex + 1);
  const newHistory = [...trimmed, structuredClone(state.layout)].slice(-MAX_HISTORY);
  return {
    ...state,
    history: newHistory,
    historyIndex: newHistory.length - 1,
  };
}

function withLayout(
  state: LayoutEditorState,
  updater: (layout: WarehouseLayout) => WarehouseLayout,
  addHistory = true
): LayoutEditorState {
  if (!state.layout) return state;
  const stateWithHistory = addHistory ? pushHistory(state) : state;
  const newLayout = updater(stateWithHistory.layout!);
  return {
    ...stateWithHistory,
    layout: newLayout,
    unplacedRacks: computeUnplaced(newLayout, stateWithHistory.rackSummaries),
  };
}

// ============================================
// Reducer
// ============================================

export function layoutReducer(state: LayoutEditorState, action: LayoutAction): LayoutEditorState {
  switch (action.type) {
    // --- Data Loading ---
    case 'LOAD_DATA_START':
      return { ...state, loading: true, error: null };

    case 'LOAD_DATA_SUCCESS': {
      const unplaced = computeUnplaced(action.layout, action.rackSummaries);
      return {
        ...state,
        layout: action.layout,
        rackSummaries: action.rackSummaries,
        unplacedRacks: unplaced,
        loading: false,
        error: null,
        history: [structuredClone(action.layout)],
        historyIndex: 0,
      };
    }

    case 'LOAD_DATA_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.error,
        layout: action.fallbackLayout,
      };

    // --- Layout Mutations ---
    case 'SET_LAYOUT': {
      const addHist = action.pushHistory !== false;
      return withLayout(state, () => action.layout, addHist);
    }

    case 'ADD_RACK':
      return withLayout(state, layout => ({
        ...layout,
        racks: [...layout.racks, action.rack],
      }));

    case 'ADD_RACKS':
      return withLayout(state, layout => ({
        ...layout,
        racks: [...layout.racks, ...action.racks],
      }));

    case 'REMOVE_RACK': {
      const s = withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.filter(r => r.rackNumber !== action.rackNumber),
      }));
      const newSel = new Set(s.editorState.selectedRacks);
      newSel.delete(action.rackNumber);
      return {
        ...s,
        editorState: {
          ...s.editorState,
          selectedRacks: newSel,
          anchorRack: s.editorState.anchorRack === action.rackNumber ? null : s.editorState.anchorRack,
        },
      };
    }

    case 'ROTATE_RACK':
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(r =>
          r.rackNumber === action.rackNumber
            ? { ...r, rotation: ((r.rotation + 90) % 360) as 0 | 90 | 180 | 270 }
            : r
        ),
      }));

    case 'MOVE_RACKS':
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(rack => {
          const update = action.updates.find(u => u.rackNumber === rack.rackNumber);
          return update ? { ...rack, x: update.x, y: update.y } : rack;
        }),
      }), false); // Don't push history on every mousemove - will push on mouseup

    case 'DELETE_SELECTED': {
      if (state.editorState.selectedRacks.size === 0) return state;
      const s = withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.filter(r => !state.editorState.selectedRacks.has(r.rackNumber)),
      }));
      return {
        ...s,
        editorState: {
          ...s.editorState,
          selectedRacks: new Set(),
          anchorRack: state.editorState.selectedRacks.has(s.editorState.anchorRack || '') ? null : s.editorState.anchorRack,
        },
      };
    }

    case 'ROTATE_SELECTED': {
      if (state.editorState.selectedRacks.size === 0) return state;
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(rack =>
          state.editorState.selectedRacks.has(rack.rackNumber)
            ? { ...rack, rotation: ((rack.rotation + 90) % 360) as 0 | 90 | 180 | 270 }
            : rack
        ),
      }));
    }

    case 'ALIGN_SELECTED_TO_GRID': {
      if (!state.layout || state.editorState.selectedRacks.size === 0) return state;
      const gridUnit = state.layout.gridUnit;
      return withLayout(state, layout => ({
        ...layout,
        racks: layout.racks.map(rack =>
          state.editorState.selectedRacks.has(rack.rackNumber)
            ? {
                ...rack,
                x: Math.round(rack.x / gridUnit) * gridUnit,
                y: Math.round(rack.y / gridUnit) * gridUnit,
              }
            : rack
        ),
      }));
    }

    case 'UPDATE_DIMENSIONS':
      return withLayout(state, layout => ({
        ...layout,
        dimensions: { ...layout.dimensions, [action.field]: action.value },
      }));

    case 'RESET_LAYOUT': {
      const s = pushHistory(state);
      return {
        ...s,
        layout: action.emptyLayout,
        unplacedRacks: computeUnplaced(action.emptyLayout, s.rackSummaries),
        editorState: { ...s.editorState, selectedRacks: new Set(), anchorRack: null },
      };
    }

    case 'IMPORT_LAYOUT': {
      const s = pushHistory(state);
      return {
        ...s,
        layout: action.layout,
        unplacedRacks: computeUnplaced(action.layout, s.rackSummaries),
      };
    }

    // --- Anchor & Selection ---
    case 'SET_ANCHOR':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          anchorRack: action.rackNumber,
        },
      };

    case 'SET_SELECTION':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          selectedRacks: action.selectedRacks,
        },
      };

    // --- Editor State ---
    case 'SET_VIEW_MODE':
      return {
        ...state,
        editorState: { ...state.editorState, viewMode: action.mode },
      };

    case 'TOGGLE_EDITOR_OPTION':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          [action.option]: !state.editorState[action.option],
        },
      };

    case 'SET_ZOOM':
      return {
        ...state,
        editorState: { ...state.editorState, zoom: action.zoom },
      };

    case 'SET_PAN_OFFSET':
      return {
        ...state,
        editorState: { ...state.editorState, panOffset: action.offset },
      };

    case 'SET_RUBBER_BAND':
      return {
        ...state,
        editorState: { ...state.editorState, rubberBand: action.rubberBand },
      };

    case 'SET_PLACEMENT_CONFIG':
      return {
        ...state,
        editorState: { ...state.editorState, placementConfig: action.config },
      };

    // --- Bulk Placement ---
    case 'SET_PREVIEW_POSITIONS':
      return { ...state, previewPositions: action.positions };

    case 'APPLY_BULK_PLACEMENT': {
      if (action.positions.length === 0) return state;
      const s = withLayout(state, layout => ({
        ...layout,
        racks: [...layout.racks, ...action.positions],
      }));
      return {
        ...s,
        selectedUnplacedRacks: [],
        previewPositions: [],
        showPlacementPanel: false,
        editorState: { ...s.editorState, anchorRack: null },
      };
    }

    case 'CANCEL_BULK_PLACEMENT':
      return {
        ...state,
        selectedUnplacedRacks: [],
        previewPositions: [],
        showPlacementPanel: false,
        editorState: { ...state.editorState, anchorRack: null },
      };

    case 'TOGGLE_UNPLACED_SELECTION': {
      const idx = state.selectedUnplacedRacks.indexOf(action.rackNumber);
      return {
        ...state,
        selectedUnplacedRacks:
          idx >= 0
            ? state.selectedUnplacedRacks.filter(r => r !== action.rackNumber)
            : [...state.selectedUnplacedRacks, action.rackNumber],
      };
    }

    case 'SELECT_ALL_UNPLACED':
      return {
        ...state,
        selectedUnplacedRacks: state.unplacedRacks.map(r => r.rackNumber),
      };

    case 'DESELECT_ALL_UNPLACED':
      return { ...state, selectedUnplacedRacks: [] };

    case 'TOGGLE_PLACEMENT_PANEL':
      return { ...state, showPlacementPanel: !state.showPlacementPanel };

    // --- Smart Layout ---
    case 'OPEN_QUICK_LAYOUT':
      return { ...state, showQuickLayout: true };

    case 'CLOSE_QUICK_LAYOUT':
      return { ...state, showQuickLayout: false, layoutMetrics: null };

    case 'APPLY_SMART_LAYOUT': {
      if (action.positions.length === 0) return state;
      const s = withLayout(state, layout => ({
        ...layout,
        racks: [...layout.racks, ...action.positions],
      }));
      return {
        ...s,
        showQuickLayout: false,
        layoutMetrics: action.metrics,
        selectedUnplacedRacks: [],
        previewPositions: [],
      };
    }

    // --- Drag from Sidebar ---
    case 'SET_SIDEBAR_DRAG_PREVIEW':
      return { ...state, sidebarDragPreview: action.preview };

    // --- UI ---
    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'SAVE_START':
      return { ...state, saveStatus: 'saving' };

    case 'SAVE_SUCCESS':
      return { ...state, saveStatus: 'saved' };

    case 'SAVE_FAILURE':
      return { ...state, saveStatus: 'error' };

    case 'TOGGLE_SETTINGS':
      return { ...state, showSettings: !state.showSettings };

    // --- Undo/Redo ---
    case 'UNDO': {
      if (state.historyIndex <= 0) return state;
      const newIndex = state.historyIndex - 1;
      const restored = structuredClone(state.history[newIndex]);
      return {
        ...state,
        layout: restored,
        historyIndex: newIndex,
        unplacedRacks: computeUnplaced(restored, state.rackSummaries),
      };
    }

    case 'REDO': {
      if (state.historyIndex >= state.history.length - 1) return state;
      const newIndex = state.historyIndex + 1;
      const restored = structuredClone(state.history[newIndex]);
      return {
        ...state,
        layout: restored,
        historyIndex: newIndex,
        unplacedRacks: computeUnplaced(restored, state.rackSummaries),
      };
    }

    default:
      return state;
  }
}

// ============================================
// Hook
// ============================================

export function useLayoutReducer() {
  const [state, dispatch] = useReducer(layoutReducer, undefined, createInitialState);

  const rackDimensionsMap = useMemo(() => {
    const map = new Map<string, RackDimensions>();
    state.rackSummaries.forEach(summary => {
      map.set(summary.rackNumber, calculateRackDimensions(summary));
    });
    return map;
  }, [state.rackSummaries]);

  const canUndo = state.historyIndex > 0;
  const canRedo = state.historyIndex < state.history.length - 1;

  return { state, dispatch, rackDimensionsMap, canUndo, canRedo };
}
